import { useEffect, useState } from "react";
import { Modal, Button, Col, Row, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChevronLeft, 
  faChevronRight, 
  faCalendarDays,
  faClock,
  faMapMarkerAlt 
} from "@fortawesome/free-solid-svg-icons";
import MeetingCard from "./MeetingCard";
import { useAtom } from "jotai";
import { leadDetailsMeetingsAtom } from "jotai/LeadsJotaiInstance";

interface EventData {
  meeting_name: string;
  meeting_start: string;
  date: string;
}

const LeadCalendar = () => {
  const [leadDetailsMeeting] = useAtom(leadDetailsMeetingsAtom);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());
  const endDate = new Date(lastDayOfMonth);
  endDate.setDate(endDate.getDate() + (6 - lastDayOfMonth.getDay()));

  // Generate calendar days
  const calendarDays = [];
  const currentDay = new Date(startDate);
  while (currentDay <= endDate) {
    calendarDays.push(new Date(currentDay));
    currentDay.setDate(currentDay.getDate() + 1);
  }

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    const dateString = date.toLocaleDateString("en-CA");
    return leadDetailsMeeting.filter((event: any) => {
      const eventDate = new Date(event.meeting_start).toLocaleDateString("en-CA");
      return eventDate === dateString;
    });
  };

  const handleDayClick = (date: Date) => {
    const events = getEventsForDate(date);
    if (events.length > 0) {
      setSelectedEvent(events);
      setModalOpen(true);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === month;
  };

  return (
    <div className="modern-calendar-container">
      <Card className="modern-calendar border-0">
        <Card.Body className="p-3">
          {/* Calendar Header */}
          <div className="calendar-header">
            <div className="header-content">
              <div className="calendar-title">
                <FontAwesomeIcon icon={faCalendarDays} className="title-icon" />
                <span>Events</span>
              </div>
            </div>
            <div className="month-navigation">
              <Button 
                variant="link" 
                className="nav-btn prev-btn"
                onClick={() => navigateMonth('prev')}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <h3 className="month-year">
                {monthNames[month]} {year}
              </h3>
              <Button 
                variant="link" 
                className="nav-btn next-btn"
                onClick={() => navigateMonth('next')}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>

          {/* Week Days Header */}
          <div className="weekdays-header">
            {weekDays.map((day) => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {calendarDays.map((date, index) => {
              const events = getEventsForDate(date);
              const hasEvents = events.length > 0;
              
              return (
                <div 
                  key={index}
                  className={`calendar-day ${isCurrentMonth(date) ? 'current-month' : 'other-month'} ${isToday(date) ? 'today' : ''} ${hasEvents ? 'has-events' : ''}`}
                  onClick={() => handleDayClick(date)}
                >
                  <span className="day-number">{date.getDate()}</span>
                  {hasEvents && (
                    <div className="event-indicators">
                      {events.slice(0, 2).map((_, i) => (
                        <div key={i} className="event-dot" />
                      ))}
                      {events.length > 2 && (
                        <span className="more-events">+{events.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Enhanced Modal */}
      <Modal 
        size="lg" 
        show={isModalOpen} 
        onHide={() => setModalOpen(false)}
        className="modern-events-modal"
        centered
      >
        <Modal.Header className="modern-modal-header">
          <Modal.Title className="d-flex align-items-center gap-3">
            <div className="modal-icon">
              <FontAwesomeIcon icon={faCalendarDays} />
            </div>
            <div>
              <h4 className="mb-0">Meeting Details</h4>
              <small className="text-muted">{selectedEvent.length} event{selectedEvent.length !== 1 ? 's' : ''} scheduled</small>
            </div>
          </Modal.Title>
          <Button 
            variant="link" 
            className="close-btn" 
            onClick={() => setModalOpen(false)}
          >
            ×
          </Button>
        </Modal.Header>

        <Modal.Body className="modern-modal-body">
          <Row className="g-4">
            {selectedEvent.map((event: any) => (
              <Col xl={6} key={event.id}>
                <MeetingCard meeting={event} />
              </Col>
            ))}
          </Row>
        </Modal.Body>
        
        <Modal.Footer className="modern-modal-footer">
          <Button 
            variant="secondary" 
            className="modern-btn"
            onClick={() => setModalOpen(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Fixed and Improved Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-calendar-container {
          --calendar-primary: #3b82f6;
          --calendar-success: #10b981;
          --calendar-warning: #f59e0b;
          --calendar-light: #f8fafc;
          --calendar-dark: #1e293b;
          --calendar-border: #e2e8f0;
          --calendar-shadow: 0 4px 20px rgba(59, 130, 246, 0.08);
          --calendar-hover: #f1f5f9;
          width: 100%;
        }

        .modern-calendar {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 16px !important;
          box-shadow: var(--calendar-shadow);
          transition: all 0.3s ease;
          overflow: hidden;
          width: 100%;
        }

        .modern-calendar:hover {
          box-shadow: 0 8px 30px rgba(59, 130, 246, 0.12);
          transform: translateY(-1px);
        }

        .calendar-header {
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--calendar-border);
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .calendar-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--calendar-dark);
        }

        .title-icon {
          width: 20px;
          height: 20px;
          color: var(--calendar-primary);
        }

        .month-navigation {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          width: 100%;
        }

        .month-year {
          font-size: 1rem;
          font-weight: 600;
          color: var(--calendar-dark);
          margin: 0;
          min-width: 120px;
          text-align: center;
          flex-shrink: 0;
        }

        .nav-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--calendar-light);
          border: 1px solid var(--calendar-border);
          color: var(--calendar-dark);
          transition: all 0.2s ease;
          padding: 0;
          text-decoration: none;
          flex-shrink: 0;
        }

        .nav-btn:hover {
          background: var(--calendar-primary);
          color: white;
          border-color: var(--calendar-primary);
          transform: scale(1.05);
        }

        .weekdays-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          margin-bottom: 0.75rem;
          padding: 0.5rem 0.25rem;
          background: var(--calendar-light);
          border-radius: 8px;
        }

        .weekday {
          text-align: center;
          font-weight: 600;
          font-size: 0.75rem;
          color: var(--calendar-dark);
          opacity: 0.7;
          padding: 0.25rem;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          width: 100%;
        }

        .calendar-day {
          position: relative;
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: white;
          border: 1px solid var(--calendar-border);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-height: 45px;
          padding: 0.25rem;
          font-size: 0.85rem;
        }

        .calendar-day:hover {
          background: var(--calendar-hover);
          border-color: var(--calendar-primary);
          transform: scale(1.02);
          z-index: 10;
        }

        .calendar-day.other-month {
          opacity: 0.4;
          background: #f9f9f9;
        }

        .calendar-day.today {
          background: linear-gradient(135deg, var(--calendar-primary), #60a5fa);
          color: white;
          border-color: var(--calendar-primary);
          font-weight: 700;
        }

        .calendar-day.today:hover {
          background: linear-gradient(135deg, #2563eb, var(--calendar-primary));
        }

        .calendar-day.has-events {
          border-color: var(--calendar-success);
          background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
        }

        .calendar-day.has-events:hover {
          background: linear-gradient(135deg, #e0f2fe, #bae6fd);
        }

        .day-number {
          font-size: 0.8rem;
          font-weight: 600;
          line-height: 1;
        }

        .event-indicators {
          display: flex;
          align-items: center;
          gap: 1px;
          margin-top: 2px;
        }

        .event-dot {
          width: 4px;
          height: 4px;
          background: var(--calendar-success);
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .calendar-day.today .event-dot {
          background: white;
        }

        .more-events {
          font-size: 0.6rem;
          font-weight: 600;
          color: var(--calendar-success);
          margin-left: 1px;
        }

        .calendar-day.today .more-events {
          color: white;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        /* Modal Styles */
        .modern-events-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(59, 130, 246, 0.15);
          overflow: hidden;
        }

        .modern-modal-header {
          background: linear-gradient(135deg, var(--calendar-primary), #60a5fa);
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        .modern-modal-header .modal-title h4 {
          color: white;
          margin: 0;
          font-weight: 700;
        }

        .modern-modal-header .modal-title small {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.875rem;
        }

        .modal-icon {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .close-btn {
          color: white !important;
          font-size: 1.5rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }

        .modern-modal-body {
          padding: 2rem;
          background: #f8fafc;
        }

        .modern-modal-footer {
          background: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        .modern-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .modern-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .calendar-day {
            min-height: 40px;
            padding: 0.2rem;
          }

          .day-number {
            font-size: 0.75rem;
          }

          .event-dot {
            width: 3px;
            height: 3px;
          }

          .more-events {
            font-size: 0.55rem;
          }

          .month-year {
            font-size: 0.9rem;
            min-width: 100px;
          }

          .nav-btn {
            width: 28px;
            height: 28px;
          }
        }

        @media (max-width: 480px) {
          .modern-calendar .card-body {
            padding: 1rem !important;
          }

          .calendar-day {
            min-height: 35px;
            border-radius: 6px;
          }

          .weekday {
            font-size: 0.7rem;
          }

          .calendar-title {
            font-size: 1rem;
          }

          .title-icon {
            width: 16px;
            height: 16px;
          }
        }
        `
      }} />
    </div>
  );
};

export default LeadCalendar;
